import { IconSearch } from '@developerz/ui';
import { For, type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import {
  type FleetViewState,
  filterPatch,
  MACHINE_FILTERS,
  type MachineFilter,
  type MachineSort,
  type MachineView,
} from '../lib/fleet-machines';
import type { MapMode } from '../lib/fleet-map';
import { MapModeSwitch } from './MapModeSwitch';
import { Switch } from './Switch';
import { Button } from './ui/Button';

// The Machines list's toolbar (#6560, prototype `mach-tools`): search, the view switch
// (List / Cards / Map, #6561 / #6562; a group of pressed buttons, not a tablist: it changes
// what the one list looks like, not which panel shows), Group by server, the filter chips
// with their counts, and the sort note, or under Map the "Show" switch in its place (the
// Map keeps the list's order, as the prototype's does, but offers no sort control of its
// own). Presenter: `FleetMachines` hands it the view state, the counts and the
// search buffer, and every press goes back up as a patch.

const FILTER_LABEL: Readonly<Record<MachineFilter, string>> = {
  all: 'fleet.machines.filter_all',
  working: 'fleet.machines.filter_working',
  idle: 'fleet.machines.filter_idle',
  attention: 'fleet.machines.filter_attention',
  offline: 'fleet.machines.filter_offline',
};

const SORT_LABEL: Readonly<Record<Exclude<MachineSort, 'default'>, string>> = {
  name: 'fleet.machines.sort_name',
  status: 'fleet.machines.sort_status',
  task: 'fleet.machines.sort_task',
  seen: 'fleet.machines.sort_seen',
  cpu: 'fleet.machines.sort_cpu',
  memory: 'fleet.machines.sort_memory',
  disk: 'fleet.machines.sort_disk',
  health: 'fleet.machines.sort_health',
};

const VIEW_LABEL: Readonly<Record<MachineView, string>> = {
  list: 'fleet.machines.view_list',
  cards: 'fleet.machines.view_cards',
  map: 'fleet.machines.view_map',
};

/** The prototype's order: List, Cards, Map. */
const VIEW_ORDER: readonly MachineView[] = ['list', 'cards', 'map'];

export type FleetMachinesToolsProps = {
  readonly view: FleetViewState;
  readonly set: (patch: Partial<FleetViewState>) => void;
  readonly counts: Readonly<Record<MachineFilter, number>>;
  /** Whether the list is grouped now (the URL's choice, else the default rule). */
  readonly grouped: boolean;
  readonly searchable: boolean;
  /** The search box's own buffer, committed to the URL after a pause. */
  readonly query: string;
  readonly onQuery: (next: string) => void;
  /** What the Map is coloured by; shown in place of the sort note under Map. */
  readonly mapMode: MapMode;
  readonly onMapMode: (mode: MapMode) => void;
};

export function FleetMachinesTools(props: FleetMachinesToolsProps): JSX.Element {
  const sorted = (): Exclude<MachineSort, 'default'> | null =>
    props.view.sort === 'default' ? null : props.view.sort;
  return (
    <div class="fleet-machines__tools">
      <div class="fleet-machines__row">
        <Show when={props.searchable}>
          <div class="search-input fleet-machines__search">
            <IconSearch size="1rem" />
            <input
              type="search"
              class="input"
              placeholder={t('fleet.machines.search_label')}
              aria-label={t('fleet.machines.search_label')}
              autocomplete="off"
              value={props.query}
              onInput={(e) => props.onQuery(e.currentTarget.value)}
            />
          </div>
        </Show>
        <fieldset class="fleet-view-switch">
          <legend class="sr-only">{t('fleet.machines.view_aria')}</legend>
          <For each={VIEW_ORDER}>
            {(v) => (
              <button
                type="button"
                class="fleet-view-switch__btn"
                aria-pressed={props.view.view === v}
                onClick={() => props.set({ view: v })}
              >
                {t(VIEW_LABEL[v])}
              </button>
            )}
          </For>
        </fieldset>
        <span class="fleet-machines__group">
          <span id="fleet-machines-group">{t('fleet.machines.group_toggle')}</span>
          <Switch
            checked={props.grouped}
            aria-labelledby="fleet-machines-group"
            onActivate={() => props.set({ group: props.grouped ? 'off' : 'on' })}
          />
        </span>
      </div>
      <div class="fleet-machines__row">
        <fieldset class="fleet-machines__chips">
          <legend class="sr-only">{t('fleet.machines.filters_aria')}</legend>
          <For each={MACHINE_FILTERS}>
            {(f) => (
              <button
                type="button"
                class="fleet-chip"
                aria-pressed={props.view.filter === f}
                onClick={() => props.set(filterPatch(f))}
              >
                {t(FILTER_LABEL[f])}
                <span class="fleet-chip__count">{props.counts[f]}</span>
              </button>
            )}
          </For>
        </fieldset>
        <Show
          when={props.view.view === 'map'}
          fallback={
            <p class="fleet-machines__sort">
              <Show when={sorted()} fallback={t('fleet.machines.sort_default')}>
                {(sort) => (
                  <>
                    {t('fleet.machines.sorted_by', { column: t(SORT_LABEL[sort()]) })}{' '}
                    <Button variant="link" size="sm" onClick={() => props.set({ sort: 'default' })}>
                      {t('fleet.machines.sort_reset')}
                    </Button>
                  </>
                )}
              </Show>
            </p>
          }
        >
          <MapModeSwitch mode={props.mapMode} onMode={props.onMapMode} />
        </Show>
      </div>
    </div>
  );
}
